/**
 * Code-book API client (/api/code-books) — Phase 28, v2.6 CTAB-03/04.
 *
 * A code book is a named, reusable collection of cross-table value mappings. A config attaches one
 * (NULL ⇒ the org's "Lab default" book, backed by the org store). The standalone Cross-Tables page
 * lists / searches / edits / clones books; the guided editor fills "needs a code" gaps with AI
 * suggestions. Mutations are admin-only (server-enforced). Mirrors configXtables.ts / orgXtables.ts.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";

// ──────────────────────── Wire types ──────────────────────────────────────────

export interface CodeBook {
  id: string;
  name: string;
  description: string;
  is_default: boolean;
  source: string;
  value_count: number;
  table_count: number;
  updated_at: string | null;
}

export interface CodeBookValue {
  id: string;
  xtable: string;
  elmnt_value: string;
  mapped_value: string;
  mapped_value2: string | null;
}

export interface CodeBookDetail {
  id: string;
  name: string;
  description: string;
  is_default: boolean;
  source: string;
  total: number;
  tables: string[]; // all distinct xtable names in the book (un-truncated; drives the editor)
  items: CodeBookValue[];
}

export interface ValueIn {
  elmnt_value: string;
  mapped_value: string;
  mapped_value2?: string | null;
}

export interface SuggestItem {
  code: string;
  confidence: number;
  method: string;
}

export interface SuggestResult {
  xtable: string;
  suggestions: Record<string, SuggestItem>;
}

// ──────────────────────── Query keys ──────────────────────────────────────────

const listKey = ["code-books"] as const;
const detailKey = (bookId: string, xtable?: string, offset = 0) =>
  ["code-books", bookId, "values", xtable ?? "", offset] as const;

// ──────────────────────── Queries ─────────────────────────────────────────────

/** GET /api/code-books — every book in the org (Lab default first). `enabled` gates non-admins. */
export function useCodeBooks(enabled = true) {
  return useQuery({
    queryKey: listKey,
    enabled,
    queryFn: async (): Promise<CodeBook[]> => {
      const { data } = await api.get<CodeBook[]>("/code-books");
      return data;
    },
  });
}

/** GET /api/code-books/{id}?xtable=&limit=&offset= — a book's detail + paged values. */
export function useCodeBookDetail(
  bookId: string | undefined,
  {
    xtable,
    offset = 0,
    limit = 50,
    enabled = true,
  }: { xtable?: string; offset?: number; limit?: number; enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: detailKey(bookId ?? "", xtable, offset),
    enabled: !!bookId && enabled,
    placeholderData: keepPreviousData, // keep the prior table visible while switching tables
    queryFn: async (): Promise<CodeBookDetail> => {
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      if (xtable) params.set("xtable", xtable);
      const { data } = await api.get<CodeBookDetail>(`/code-books/${bookId}?${params}`);
      return data;
    },
  });
}

// ──────────────────────── Mutations ───────────────────────────────────────────

function useInvalidate() {
  const qc = useQueryClient();
  return (bookId?: string) => {
    void qc.invalidateQueries({ queryKey: listKey });
    if (bookId) void qc.invalidateQueries({ queryKey: ["code-books", bookId] });
  };
}

/** POST /api/code-books — create a named book (admin). */
export function useCreateCodeBook() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (payload: { name: string; description?: string }): Promise<CodeBook> => {
      const { data } = await api.post<CodeBook>("/code-books", payload);
      return data;
    },
    onSuccess: () => invalidate(),
  });
}

/**
 * POST /api/code-books/from-config — promote a config's per-config cross-table values into a NEW
 * named book (admin). By default the new book is attached to the config, so its code_book_id changes.
 */
export function useCreateBookFromConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      config_id: string;
      name: string;
      description?: string;
      attach?: boolean;
    }): Promise<CodeBook> => {
      const { data } = await api.post<CodeBook>("/code-books/from-config", payload);
      return data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: listKey });
      void qc.invalidateQueries({ queryKey: ["config", vars.config_id] });
      void qc.invalidateQueries({ queryKey: ["configs"] });
    },
  });
}

/** PUT /api/code-books/{id} — rename / edit description (admin). */
export function useUpdateCodeBook() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      name?: string;
      description?: string;
    }): Promise<CodeBook> => {
      const { id, ...body } = args;
      const { data } = await api.put<CodeBook>(`/code-books/${id}`, body);
      return data;
    },
    onSuccess: (_d, args) => invalidate(args.id),
  });
}

/** DELETE /api/code-books/{id} — delete a non-default book (admin). */
export function useDeleteCodeBook() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await api.delete(`/code-books/${id}`);
    },
    onSuccess: () => invalidate(),
  });
}

/** POST /api/code-books/{id}/clone — deep-copy into a new named book (admin). */
export function useCloneCodeBook() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (args: { id: string; name: string }): Promise<CodeBook> => {
      const { data } = await api.post<CodeBook>(`/code-books/${args.id}/clone`, {
        name: args.name,
      });
      return data;
    },
    onSuccess: () => invalidate(),
  });
}

/** POST /api/code-books/{id}/values — upsert (elmnt → code) rows for one xtable (admin). */
export function useUpsertCodeBookValues(bookId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (payload: { xtable: string; values: ValueIn[] }): Promise<{
      written: number;
    }> => {
      const { data } = await api.post(`/code-books/${bookId}/values`, payload);
      return data;
    },
    onSuccess: () => invalidate(bookId),
  });
}

/** DELETE /api/code-books/{id}/values/{vid} — remove one value (admin). */
export function useDeleteCodeBookValue(bookId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (valueId: string): Promise<void> => {
      await api.delete(`/code-books/${bookId}/values/${valueId}`);
    },
    onSuccess: () => invalidate(bookId),
  });
}

/** PUT /api/code-books/attach — attach (or detach with null) a book to a config (admin). */
export function useAttachCodeBook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      config_id: string;
      code_book_id: string | null;
    }): Promise<void> => {
      await api.put("/code-books/attach", args);
    },
    onSuccess: (_d, args) => {
      void qc.invalidateQueries({ queryKey: ["config", args.config_id] });
      void qc.invalidateQueries({ queryKey: ["configs"] });
    },
  });
}

/**
 * GET /api/code-books/attached/{config_id} — the config's attached cross-tables, in priority order
 * (first id wins on overlapping values). Empty ⇒ the format uses the Lab default (org store).
 */
export function useAttachedCodeBooks(configId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["config", configId ?? "", "attached-code-books"],
    enabled: !!configId && enabled,
    queryFn: async (): Promise<string[]> => {
      const { data } = await api.get<{ code_book_ids: string[] }>(
        `/code-books/attached/${configId}`,
      );
      return data.code_book_ids;
    },
  });
}

/**
 * PUT /api/code-books/attach-set — replace the config's attached cross-tables with an ordered set
 * (admin). Order = priority (first wins on overlap); an empty list reverts the format to the Lab
 * default. Overlapping keys across the set are deduped server-side, first-wins.
 */
export function useAttachCodeBookSet() {
  const qc = useQueryClient();
  return useMutation({
    // PUT /code-books/attach-set is 204 No Content — the persisted set is read back via
    // useAttachedCodeBooks (invalidated below), so this resolves to void.
    mutationFn: async (args: { config_id: string; code_book_ids: string[] }): Promise<void> => {
      await api.put("/code-books/attach-set", args);
    },
    onSuccess: (_d, args) => {
      void qc.invalidateQueries({ queryKey: ["config", args.config_id] });
      void qc.invalidateQueries({ queryKey: ["config", args.config_id, "attached-code-books"] });
      void qc.invalidateQueries({ queryKey: ["configs"] });
    },
  });
}

/**
 * POST /api/org/cross-tables/split — split the merged Lab default into NAMED cross-tables, one per
 * source workbook the lab imported (admin). They then become selectable above. Refreshes the list.
 */
export function useSplitLabDefault() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (): Promise<{
      books: { id: string; name: string; tables: number; values: number }[];
    }> => {
      const { data } = await api.post("/org/cross-tables/split");
      return data;
    },
    onSuccess: () => invalidate(),
  });
}

/** POST /api/code-books/{id}/suggest — AI-suggested codes for unmapped values (propose-only, admin). */
export function useSuggestValues(bookId: string) {
  return useMutation({
    mutationFn: async (payload: {
      xtable: string;
      values: string[];
      use_llm?: boolean;
    }): Promise<SuggestResult> => {
      const { data } = await api.post<SuggestResult>(`/code-books/${bookId}/suggest`, payload);
      return data;
    },
  });
}

// ──────────────────────── Phase 29: jumpstart / import-file / community share ──────────────────

/** POST /api/code-books/{id}/jumpstart — seed a book from a regulator spec's known codes (admin). */
export function useJumpstartCodeBook(bookId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (payload: {
      spec_id: string;
    }): Promise<{ seeded: Record<string, number>; total: number }> => {
      const { data } = await api.post(`/code-books/${bookId}/jumpstart`, payload);
      return data;
    },
    onSuccess: () => invalidate(bookId),
  });
}

/** POST /api/code-books/{id}/import-file — build mappings from an uploaded CSV (admin, multipart). */
export function useImportCodeBookFile(bookId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (args: {
      file: File;
      xtable?: string;
    }): Promise<{ written: number; xtables: string[]; skipped: number }> => {
      const form = new FormData();
      form.append("file", args.file);
      if (args.xtable) form.append("xtable", args.xtable);
      // The shared axios instance defaults Content-Type to application/json; override for multipart.
      const { data } = await api.post(`/code-books/${bookId}/import-file`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    },
    onSuccess: () => invalidate(bookId),
  });
}

/** POST /api/code-books/{id}/publish — share a book to the community pool, tagged by regulator (admin). */
export function usePublishCodeBook(bookId: string) {
  return useMutation({
    mutationFn: async (payload: {
      regulator: string;
    }): Promise<{ contributed: number; tables: number }> => {
      const { data } = await api.post(`/code-books/${bookId}/publish`, payload);
      return data;
    },
  });
}

/** POST /api/code-books/{id}/import-community — pull community codes for a regulator into the book (admin). */
export function useImportCommunityCodes(bookId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (payload: {
      regulator: string;
      xtable?: string | null;
    }): Promise<{ written: number; xtables: string[] }> => {
      const { data } = await api.post(`/code-books/${bookId}/import-community`, payload);
      return data;
    },
    onSuccess: () => invalidate(bookId),
  });
}
